
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import Header from "@/components/Header";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { PLATFORM_FEE_COLLECTOR, getReferralCodes, addReferralCode, type ReferralCode } from "@/utils/referralSystem";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, ExternalLink } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { REFERRAL_REGISTRY_ADDRESS, referralCodeHash, referralRegistryAbi } from "@/utils/referralRegistry";

const ReferralCodes = () => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { toast } = useToast();
  const [newCode, setNewCode] = useState("");
  const [referrerAddress, setReferrerAddress] = useState("");
  const [expiryDate, setExpiryDate] = useState<Date>();
  const [codes, setCodes] = useState<ReferralCode[]>([]);

  useEffect(() => {
    setCodes(getReferralCodes());
  }, []);

  // Only allow access to platform fee collector
  if (address?.toLowerCase() !== PLATFORM_FEE_COLLECTOR.toLowerCase()) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-24">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-red-500">Access Denied</h1>
            <p className="mt-4">Only the platform fee collector can access this page.</p>
          </div>
        </main>
      </div>
    );
  }

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCode || !referrerAddress) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    const success = addReferralCode({
      code: newCode,
      referrerAddress: referrerAddress as `0x${string}`,
      isActive: true,
      expiryDate,
    });

    if (success) {
      if (REFERRAL_REGISTRY_ADDRESS && walletClient && publicClient && address) {
        try {
          const expiry = expiryDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
          const hash = await walletClient.writeContract({
            address: REFERRAL_REGISTRY_ADDRESS,
            abi: referralRegistryAbi,
            functionName: "registerCode",
            args: [referralCodeHash(newCode), referrerAddress as `0x${string}`, BigInt(Math.floor(expiry.getTime() / 1000))],
          });
          await publicClient.waitForTransactionReceipt({ hash });
        } catch (error) {
          toast({ title: "On-chain registration failed", description: "The local code was saved, but the blockchain transaction did not complete.", variant: "destructive" });
          return;
        }
      }
      toast({
        title: "Success",
        description: `Created referral code: ${newCode} for address: ${referrerAddress}`,
      });
      setCodes(getReferralCodes()); // Refresh the list
      setNewCode("");
      setReferrerAddress("");
      setExpiryDate(undefined);
    } else {
      toast({
        title: "Error",
        description: "Failed to create referral code. It might already exist.",
        variant: "destructive",
      });
    }
  };

  const getPolygonscanUrl = (address: string) => {
    return `https://polygonscan.com/address/${address}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24">
        <div className="max-w-2xl mx-auto space-y-8">
          <h1 className="text-3xl font-bold mb-8">Manage Referral Codes</h1>
          
          <Card>
            <CardHeader>
              <CardTitle>Create New Referral Code</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateCode} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="code" className="text-sm font-medium">
                    Referral Code
                  </label>
                  <Input
                    id="code"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    placeholder="Enter referral code (e.g., LAUNCH2024)"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="address" className="text-sm font-medium">
                    Referrer Address
                  </label>
                  <Input
                    id="address"
                    value={referrerAddress}
                    onChange={(e) => setReferrerAddress(e.target.value)}
                    placeholder="Enter referrer's wallet address"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Expiry Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {expiryDate ? format(expiryDate, "PPP") : "Pick an expiry date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={expiryDate}
                        onSelect={setExpiryDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button type="submit">
                  Create Referral Code
                </Button>
              </form>
            </CardContent>
          </Card>
          {!REFERRAL_REGISTRY_ADDRESS && (
            <p className="text-sm text-amber-600">On-chain registry not configured. Set VITE_REFERRAL_REGISTRY_ADDRESS after deployment to issue blockchain-backed codes.</p>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Active Referral Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {codes.map((code) => (
                  <div key={code.code} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{code.code}</p>
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <a
                            href={getPolygonscanUrl(code.referrerAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center hover:text-primary"
                          >
                            View on Polygonscan
                            <ExternalLink className="ml-1 h-3 w-3" />
                          </a>
                        </div>
                        {code.expiryDate && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Expires: {format(new Date(code.expiryDate), "PPP")}
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {code.createdAt && format(new Date(code.createdAt), "PPP")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ReferralCodes;
